import {
  createEntityAdapter,
  EntityAdapter,
  EntityState,
} from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';

import { ProjectContactModel } from '../../models/project-contact.model';
import { ProjectContactActions } from './project-contact.actions';

export interface ProjectContactState
  extends EntityState<ProjectContactModel.Document> {
  isLoading: boolean;
  hasLoaded: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  deletingContactIds: string[];
}

export const projectContactAdapter: EntityAdapter<ProjectContactModel.Document> =
  createEntityAdapter<ProjectContactModel.Document>({
    selectId: (contact) => contact._id,
    sortComparer: (a, b) => a.name.localeCompare(b.name),
  });

const initialState: ProjectContactState = projectContactAdapter.getInitialState({
  isLoading: false,
  hasLoaded: false,
  isCreating: false,
  isUpdating: false,
  deletingContactIds: [],
});

export const projectContactStateReducer = createReducer(
  initialState,
  on(ProjectContactActions.resetToInitialState, () => {
    return {
      ...initialState,
    };
  }),
  on(ProjectContactActions.loadContacts, (state) => {
    return projectContactAdapter.removeAll({
      ...state,
      isLoading: true,
      hasLoaded: false,
    });
  }),
  on(ProjectContactActions.loadContactsSuccess, (state, { contacts }) => {
    return projectContactAdapter.setAll(contacts, {
      ...state,
      isLoading: false,
      hasLoaded: true,
    });
  }),
  on(ProjectContactActions.loadContactsFail, (state) => {
    return {
      ...state,
      isLoading: false,
      hasLoaded: true,
    };
  }),
  on(ProjectContactActions.createContact, (state) => {
    return {
      ...state,
      isCreating: true,
    };
  }),
  on(ProjectContactActions.createContactSuccess, (state, { contact }) => {
    return projectContactAdapter.upsertOne(contact, {
      ...state,
      isCreating: false,
    });
  }),
  on(ProjectContactActions.createContactFail, (state) => {
    return {
      ...state,
      isCreating: false,
    };
  }),
  on(ProjectContactActions.createContacts, (state) => {
    return {
      ...state,
      isCreating: true,
    };
  }),
  on(ProjectContactActions.createContactsSuccess, (state, { contacts }) => {
    return projectContactAdapter.upsertMany(contacts, {
      ...state,
      isCreating: false,
    });
  }),
  on(ProjectContactActions.createContactsFail, (state) => {
    return {
      ...state,
      isCreating: false,
    };
  }),
  on(ProjectContactActions.updateContact, (state) => {
    return {
      ...state,
      isUpdating: true,
    };
  }),
  on(ProjectContactActions.updateContactSuccess, (state, { contact }) => {
    return projectContactAdapter.upsertOne(contact, {
      ...state,
      isUpdating: false,
    });
  }),
  on(ProjectContactActions.updateContactFail, (state) => {
    return {
      ...state,
      isUpdating: false,
    };
  }),
  on(ProjectContactActions.deleteContact, (state, { contactId }) => {
    return {
      ...state,
      deletingContactIds: state.deletingContactIds.includes(contactId)
        ? state.deletingContactIds
        : [...state.deletingContactIds, contactId],
    };
  }),
  on(ProjectContactActions.deleteContactSuccess, (state, { contactId }) => {
    return projectContactAdapter.removeOne(contactId, {
      ...state,
      deletingContactIds: state.deletingContactIds.filter((id) => id !== contactId),
    });
  }),
  on(ProjectContactActions.deleteContactFail, (state, { contactId }) => {
    return {
      ...state,
      deletingContactIds: state.deletingContactIds.filter((id) => id !== contactId),
    };
  }),
);

export const projectContactFeature = createFeature({
  name: 'projectContact',
  reducer: projectContactStateReducer,
  extraSelectors: ({ selectProjectContactState }) => {
    const { selectAll, selectEntities } =
      projectContactAdapter.getSelectors(selectProjectContactState);

    return {
      selectProjectContacts: selectAll,
      selectProjectContactEntities: selectEntities,
    };
  },
});
